import { Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ApiErrorFieldEnum } from "@models/http/http-options";
import { BehaviorSubject, of, take, filter, skip } from "rxjs";
import { catchError, switchMap, tap } from "rxjs/operators";
import { NotificationsHubService } from "@services/notifications/notifications-hub.service";
import { BaseModalComponent } from "@modals/base-modal/base-modal.component";
import { INotificationResponse } from "@dmms/shared/data-access/notification/models/notification";
import { NotificationService } from "@dmms/core/notification";
import { NotificationApiService } from "@dmms/shared/data-access/notification/services/notification-api.service";
import { IPagination } from "@models/core/pagination";
import { HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { DateFormatEnum } from "@models/core/date-format";

interface INotificationParams extends IPagination {
  isNotRead: boolean;
  //local
  withLoading?: boolean;
}

@Component({
  selector: "app-notifications-modal",
  templateUrl: "./notifications-modal.component.html",
  styleUrl: "./notifications-modal.component.scss",
  standalone: false,
})
export class NotificationsModalComponent
  extends BaseModalComponent<NotificationsModalComponent, string>
  implements OnInit
{
  loading = true;
  notifications: INotificationResponse[] = [];
  allCount = 0;
  unreadCount = 0;
  total = 0;
  activeTab: "all" | "unread" = "all";

  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationApiService = inject(NotificationApiService);
  private readonly notificationService = inject(NotificationService);
  private readonly notificationsHubService = inject(NotificationsHubService);
  protected readonly dateFormatEnum = DateFormatEnum;

  readonly filter$ = new BehaviorSubject<INotificationParams>({
    pageIndex: 1,
    pageSize: +localStorage.getItem("pageSize") || 10,
    isNotRead: false,
    withLoading: true,
  });

  ngOnInit(): void {
    this.filter$
      .pipe(
        tap((params) => {
          if (params.withLoading) {
            this.loading = true;
          }
        }),
        switchMap(({ withLoading, ...apiParams }) => {
          return this.notificationApiService.getNotifications(apiParams).pipe(
            catchError((err: HttpErrorResponse) => {
              this.notificationService.findAndShowErrors(
                err,
                ApiErrorFieldEnum.Code,
              );
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.notifications = response?.value?.items || [];
        this.total = response?.value?.totalCount || 0;
        this.unreadCount = response?.value?.unreadCount || 0;
        this.allCount = response?.value?.totalCount || 0;
        this.loading = false;
      });

    this.notificationsHubService.counts$
      .pipe(
        skip(1),
        takeUntilDestroyed(this.destroyRef),
        filter((hubData) => this.allCount < hubData.totalCount),
      )
      .subscribe(() => {
        this.reloadNotifications();
      });
  }

  private reloadNotifications(): void {
    this.filter$.next({
      ...this.filter$.value,
      withLoading: false,
    });
  }

  switchTab(tab: "all" | "unread"): void {
    if (this.activeTab === tab) {
      return;
    }

    this.activeTab = tab;
    this.filter$.next({
      ...this.filter$.value,
      pageIndex: 1,
      isNotRead: tab === "unread",
      withLoading: true,
    });
  }

  navigateToLink(notification: INotificationResponse): void {
    if (notification.linkUrl) {
      this.router.navigateByUrl(notification.linkUrl);
      this.closeModal();
    }
  }

  setPage(pageIndex: number): void {
    this.filter$.next({
      ...this.filter$.value,
      pageIndex,
      withLoading: true,
    });
  }

  setPageSize(pageSize: number): void {
    if (pageSize == this.filter$.value.pageSize) {
      return;
    }

    this.filter$.next({
      ...this.filter$.value,
      pageIndex: 1,
      pageSize,
      withLoading: true,
    });
  }

  protected setNotificationAsRead(id: string): void {
    this.notificationApiService
      .markNotificationAsRead(id)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          this.notificationService.findAndShowErrors(
            err,
            ApiErrorFieldEnum.Code,
          );
          return of(null);
        }),
        take(1),
      )
      .subscribe((res) => {
        if (res?.isSuccess) {
          this.filter$.next({
            ...this.filter$.value,
            withLoading: false,
          });

          this.notificationService.success("ui.form.editSuccess");
        }
      });
  }

  protected setAllNotificationAsRead(): void {
    this.notificationApiService
      .markAllNotificationsAsRead()
      .pipe(
        catchError((err: HttpErrorResponse) => {
          this.notificationService.findAndShowErrors(
            err,
            ApiErrorFieldEnum.Code,
          );
          return of(null);
        }),
        take(1),
      )
      .subscribe((res) => {
        if (res?.isSuccess) {
          this.filter$.next({
            ...this.filter$.value,
            withLoading: false,
          });

          this.notificationService.success(
            "notification.recordsIsSuccessfullyUpdated",
          );
        }
      });
  }

  closeModal(): void {
    this.dialogRef.close();
  }
}
